import React, { Ref, useEffect } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, CardProps, CardHeaderProps, CardContentProps, CardHeader } from '@mui/material';

// project imports
import { KeyedObject } from 'types';
import { useDispatch } from 'react-redux';
import { updateSectionName } from 'store/slices/section';

// constant
const headerSX = {
    '& .MuiCardHeader-action': { mr: 0 }
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps extends KeyedObject {
    border?: boolean;
    boxShadow?: boolean;
    children: React.ReactNode | string;
    style?: React.CSSProperties;
    content?: boolean;
    className?: string;
    contentClass?: string;
    contentSX?: CardContentProps['sx'];
    darkTitle?: boolean;
    sx?: CardProps['sx'];
    secondary?: CardHeaderProps['action'];
    shadow?: string;
    elevation?: number;
    title?: React.ReactNode | string;
    headerOptions?: React.ReactNode;
}

const MainCard = React.forwardRef(
    (
        {
            border = true,
            boxShadow,
            children,
            content = true,
            contentClass = '',
            contentSX = {},
            darkTitle,
            secondary,
            shadow,
            sx = {},
            title,
            headerOptions,
            ...others
        }: MainCardProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const dispatch = useDispatch();
        const theme = useTheme();

        useEffect(() => {
            dispatch(
                updateSectionName({
                    sectionName: title
                })
            );
        }, [dispatch, title]);

        return (
            <Card
                ref={ref}
                {...others}
                sx={{
                    paddingLeft: '1rem',
                    border: 'none',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
                    ':hover': {
                        boxShadow: boxShadow
                            ? shadow ||
                              (theme.palette.mode === 'dark' ? '0 2px 14px 0 rgb(33 150 243 / 10%)' : '0 2px 14px 0 rgb(32 40 45 / 8%)')
                            : 'inherit'
                    },
                    ...sx
                }}
            >
                {/* card header and action */}
                {headerOptions && <CardHeader sx={headerSX} title={headerOptions} action={secondary} />}

                {/* card content */}
                {content && (
                    <CardContent sx={contentSX} className={contentClass}>
                        {children}
                    </CardContent>
                )}
                {!content && children}
            </Card>
        );
    }
);

export default MainCard;
